import { useEffect, useRef, useState } from 'react';
import { B, font, weight } from '../../constants/brand';
import { api } from '../../api/client';
import { Icon } from '../ui';
import { HeaderIconButton } from './HeaderIconButton';

// The header's own buttons that need a count or a menu: Mail, Tasks and the
// account avatar. The bell and the feedback button render HeaderIconButton
// themselves, since they own their dropdown and modal.

const MAIL_POLL_MS = 120000;

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

// Mail: the Inbox's unread count. Hidden count (not an error) when the person
// has not connected Outlook — the endpoint answers available:false.
export function HeaderMailButton({ onOpen }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => {
      if (!isVisible()) return;
      api.get('mail/unread-count')
        .then((r) => { if (alive) setUnread(r?.available ? Number(r.unread) || 0 : 0); })
        .catch(() => {});
    };
    const first = setTimeout(load, 2500);
    const t = setInterval(load, MAIL_POLL_MS);
    const onVis = () => { if (isVisible()) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; clearTimeout(first); clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, []);
  return <HeaderIconButton icon="mail" label="Mail" count={unread} ariaLabel={unread ? `Mail (${unread} unread)` : 'Mail'} onClick={onOpen} />;
}

// Tasks: how many task suggestions Harvi found in notes are waiting for review.
// With some waiting, the button opens that review; with none, the Tasks page.
export function HeaderTasksButton({ onOpenTasks }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const onCount = (e) => setCount(Number(e?.detail?.count) || 0);
    window.addEventListener('harvus-task-proposals-count', onCount);
    return () => window.removeEventListener('harvus-task-proposals-count', onCount);
  }, []);
  return (
    <HeaderIconButton icon="tasks" label={count ? 'Task suggestions to review' : 'Tasks'} count={count}
      onClick={() => {
        if (count) window.dispatchEvent(new CustomEvent('harvus-open-task-proposals'));
        else onOpenTasks?.();
      }} />
  );
}

// The avatar: who is signed in, My settings, Sign out.
export function HeaderAccountMenu({ user, onSettings, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const item = {
    width: '100%', textAlign: 'left', background: 'none', border: 'none', fontFamily: 'inherit',
    fontSize: font.base, color: B.text, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 10,
  };
  // Neil's view-as switch is stored server-side and applied to every request,
  // so the page reloads to pick up the new role everywhere at once.
  const [switching, setSwitching] = useState(false);
  const switchView = async (role) => {
    setSwitching(true);
    try { await api.put('view-as', { role }); window.location.reload(); }
    catch (e) { setSwitching(false); window.alert(e?.message || 'Could not switch view.'); }
  };
  const hoverOn = (e) => { e.currentTarget.style.background = B.surfaceMuted; };
  const hoverOff = (e) => { e.currentTarget.style.background = 'none'; };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Account menu"
        title={user?.displayName}
        onClick={() => setOpen((o) => !o)}
        style={{
          width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0,
          background: B.navy, color: B.white, fontFamily: 'inherit', fontSize: 13, fontWeight: weight.bold,
          boxShadow: open ? B.ringAccent : 'none',
        }}>
        {user?.initials || '?'}
      </button>
      {open ? (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 240, background: B.surface,
          border: `1px solid ${B.divider}`, borderRadius: 12, boxShadow: B.shadowXl, padding: 6, zIndex: 120,
        }}>
          <div style={{ padding: '8px 10px 10px', borderBottom: `1px solid ${B.divider}`, marginBottom: 4, minWidth: 0 }}>
            <div style={{ fontSize: font.base, fontWeight: weight.semibold, color: B.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.displayName}</div>
            {user?.email ? <div style={{ fontSize: font.sm, color: B.textMd, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div> : null}
            {user?.role ? <div style={{ fontSize: font.xs, color: user.viewingAs ? B.amber : B.textLt, marginTop: 2 }}>
              {user.viewingAs ? `Viewing as ${user.viewingAs} (you are ${user.realRole || 'Admin'})` : user.role}
            </div> : null}
          </div>
          {user?.canViewAs ? (
            <button type="button" role="menuitem" style={item} onMouseEnter={hoverOn} onMouseLeave={hoverOff}
              disabled={switching}
              onClick={() => switchView(user.viewingAs ? null : 'User')}>
              <Icon name="users" size={15} /> {user.viewingAs ? `Back to ${user.realRole || 'Admin'} view` : 'View as User'}
            </button>
          ) : null}
          <button type="button" role="menuitem" style={item} onMouseEnter={hoverOn} onMouseLeave={hoverOff}
            onClick={() => { setOpen(false); onSettings?.(); }}>
            <Icon name="settings" size={15} /> My settings
          </button>
          <button type="button" role="menuitem" style={item} onMouseEnter={hoverOn} onMouseLeave={hoverOff}
            onClick={() => { setOpen(false); onSignOut?.(); }}>
            <Icon name="signOut" size={15} /> Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
